import { useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Mail, Phone, MapPin, Send, CheckCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitted(true);
      toast.success("Message sent successfully!");
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-[#09090b] py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-8 text-center">
          <Mail className="w-16 h-16 text-[#FFC72C] mx-auto mb-6" />
          <h1 className="font-heading text-5xl md:text-6xl font-bold uppercase text-white mb-4">
            Contact Us
          </h1>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            Have questions or want to get in touch with ABFA? We'd love to hear from you.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Info */}
            <div>
              <h2 className="font-heading text-3xl font-bold uppercase text-[#09090b] mb-8">
                Get in Touch
              </h2>
              
              <div className="space-y-6 mb-12">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#FFC72C]">
                    <MapPin className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg uppercase text-[#09090b] mb-1">Address</h3>
                    <p className="text-gray-600">
                      ABFA Technical Centre<br />
                      Paynters Road<br />
                      St. John's, Antigua
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#FFC72C]">
                    <Phone className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg uppercase text-[#09090b] mb-1">Phone</h3>
                    <a href="tel:+12685626014" className="text-gray-600 hover:text-[#009E60]" data-testid="contact-phone-link">
                      +1 (268) 562-6014
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-[#FFC72C]">
                    <Mail className="w-6 h-6 text-black" />
                  </div>
                  <div>
                    <h3 className="font-heading font-bold text-lg uppercase text-[#09090b] mb-1">Email</h3>
                    <a href="mailto:gensec@footballanu.org" className="text-gray-600 hover:text-[#009E60]">
                      gensec@footballanu.org
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-gray-100 p-6">
                <h3 className="font-heading font-bold text-lg uppercase text-[#09090b] mb-4">Office Hours</h3>
                <div className="space-y-2 text-gray-600">
                  <p className="flex justify-between">
                    <span>Monday - Friday</span>
                    <span className="font-medium">8:00 AM - 4:00 PM</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Saturday</span>
                    <span className="font-medium">9:00 AM - 12:00 PM</span>
                  </p>
                  <p className="flex justify-between">
                    <span>Sunday</span>
                    <span className="font-medium">Closed</span>
                  </p>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <div className="bg-white border border-gray-200 p-8">
                <h2 className="font-heading text-2xl font-bold uppercase text-[#09090b] mb-6">
                  Send us a Message
                </h2>

                {submitted ? (
                  <div className="text-center py-12" data-testid="contact-success">
                    <CheckCircle className="w-16 h-16 text-[#009E60] mx-auto mb-4" />
                    <h3 className="font-heading font-bold text-xl uppercase text-[#09090b] mb-2">
                      Message Sent!
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Thank you for contacting ABFA. We will get back to you soon.
                    </p>
                    <button
                      onClick={() => setSubmitted(false)}
                      className="btn-abfa-secondary"
                    >
                      Send Another Message
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Your Name *
                      </label>
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="form-input-abfa"
                        placeholder="John Doe"
                        data-testid="contact-name-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="form-input-abfa"
                        placeholder="john@example.com"
                        data-testid="contact-email-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Subject *
                      </label>
                      <Input
                        type="text"
                        name="subject"
                        value={formData.subject}
                        onChange={handleChange}
                        required
                        className="form-input-abfa"
                        placeholder="How can we help?"
                        data-testid="contact-subject-input"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Message *
                      </label>
                      <Textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={5}
                        className="form-input-abfa resize-none"
                        placeholder="Your message..."
                        data-testid="contact-message-input"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={loading}
                      className="btn-abfa-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="contact-submit-btn"
                    >
                      <span className="flex items-center justify-center gap-2">
                        {loading ? (
                          <>
                            <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="w-5 h-5" />
                            Send Message
                          </>
                        )}
                      </span>
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="h-[400px] bg-gray-200">
        <iframe
          src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3875.6231651561!2d-61.79097!3d17.11742!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMTfCsDA3JzAyLjciTiA2McKwNDcnMjcuNSJX!5e0!3m2!1sen!2s!4v1234567890"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen=""
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title="ABFA Location"
        />
      </section>
    </div>
  );
};

export default ContactPage;
